@if (loading()) {
<app-page-loader loadingType="content" />
} @else {
<app-survey-header [survey]="survey"></app-survey-header>

<div class="p-3 h-[calc(100%-var(--spacing)*15)] overflow-y-auto">
  <app-breadcrumb [items]="[{ label: 'Surveys', route: '/surveys' },{ label: 'Fieldworks' }]"/>

  @if (error()) {
  <p class="mt-3 rounded-lg border border-red-200 bg-red-100 p-3 text-sm text-red-600">{{ error() }}</p>
  }

  @if (survey) {
  <div class="mt-3 rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
    <div class="border-b border-stroke px-5 py-4 dark:border-strokedark sm:px-7.5">
      <h4 class="text-lg font-semibold text-gray-800 dark:text-gray-300">Fieldworks</h4>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
        When and how this survey's invitations and reminders get dispatched to respondents.
      </p>
    </div>

    <div class="divide-y divide-gray-100 p-5 dark:divide-gray-800 sm:p-7.5">

      <!-- Dispatch delay by segment -->
      <div class="pb-6 first:pt-0 last:pb-0">
        <h5 class="mb-1 flex items-center gap-1.5 text-base font-semibold text-black dark:text-white">
          Dispatch Delay by Segment
          <app-info-tooltip>
            The reference date fieldwork counts from, per segment. "Import Date" is when the respondent data was
            loaded; "Event Date" is the date of the underlying event (e.g. purchase, service visit). The dispatch
            schedule below starts counting from this date plus the number of delay days shown here.
          </app-info-tooltip>
        </h5>

        <div class="mt-3 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table class="w-full text-left text-sm text-gray-700 dark:text-gray-300">
            <thead class="bg-gray-50 text-xs uppercase text-gray-500 dark:bg-gray-800 dark:text-gray-400">
              <tr>
                <th class="px-4 py-2.5">Segment</th>
                <th class="px-4 py-2.5">Base Date</th>
                <th class="px-4 py-2.5">Delay</th>
              </tr>
            </thead>
            <tbody>
              @for (row of delayRows; track row.segment) {
              <tr class="border-t border-gray-100 dark:border-gray-800"
                [class.bg-blue-50]="row.segment === 'Root'" [class.dark:bg-gray-700]="row.segment === 'Root'">
                <td class="px-4 py-2.5 font-medium text-gray-800 dark:text-gray-200">
                  {{ row.segment }}
                  @if (row.segment === 'Root') {
                  <span class="ml-1.5 rounded-full bg-gray-200 px-2 py-0.5 text-xs font-normal text-gray-600 dark:bg-gray-700 dark:text-gray-300">All segments</span>
                  }
                </td>
                <td class="px-4 py-2.5">{{ row.dateLabel }}</td>
                <td class="px-4 py-2.5">
                  @if (row.days > 0) {
                  <span>{{ row.days }} day{{ row.days === 1 ? '' : 's' }} after</span>
                  } @else {
                  <span class="text-gray-500 dark:text-gray-400">Same day</span>
                  }
                </td>
              </tr>
              } @empty {
              <tr><td colspan="3" class="px-4 py-6 text-center text-gray-400">No segment delay rules defined.<br>The default rule is to dispatch for all segments (<b>Root</b>) in same day (<b>no delay</b>) of import (<b>ImportDate</b>).</td></tr>
              }
            </tbody>
          </table>
        </div>
      </div>

      <!-- Weekly dispatch days -->
      <div class="py-6">
        <h5 class="mb-1 flex items-center gap-1.5 text-base font-semibold text-black dark:text-white">
          Weekly Dispatch Days
          <app-info-tooltip>
            The days of the week dispatch is allowed to go out on. If a scheduled dispatch date falls outside these
            days, it's held until the next allowed day. Assumes 1 = Monday … 7 = Sunday.
          </app-info-tooltip>
        </h5>

        <div class="mt-3 flex flex-wrap items-center gap-1.5">
          @for (day of weekDayNames; track day) {
          <span class="rounded-full border border-brand-200 bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700 dark:border-brand-500/30 dark:bg-brand-500/10 dark:text-brand-400">{{ day }}</span>
          } @empty {
          <span class="text-sm text-gray-400">Not defined.</span>
          }
          @if (weekDaysCode) {
          <span class="ml-1 text-xs text-gray-400" [title]="'Raw value: ' + weekDaysCode">({{ weekDaysCode }})</span>
          }
        </div>
      </div>

      <!-- Dispatch schedule -->
      <div class="py-6">
        <h5 class="mb-1 flex items-center gap-1.5 text-base font-semibold text-black dark:text-white">
          Dispatch Schedule
          <app-info-tooltip>
            Each step is a dispatch (the first invitation, then reminders), counted in days after the segment's
            delay-adjusted base date above. For example "Day 3" goes out 3 days after that date, using the
            channel(s) and SMS provider listed.
          </app-info-tooltip>
        </h5>

        <div class="mt-3 space-y-2">
          @for (rule of scheduleRules; track rule.id) {
          <div class="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800/50">
            <div class="flex h-9 w-16 shrink-0 items-center justify-center rounded-lg bg-white text-xs font-semibold text-gray-600 dark:bg-gray-900 dark:text-gray-300">
              Day {{ rule.id }}
            </div>
            <div class="flex flex-wrap items-center gap-1.5">
              @for (channel of rule.channels; track channel) {
              <span class="rounded-full px-2.5 py-0.5 text-xs font-medium"
                [class.bg-blue-100]="channel === 'email'" [class.text-blue-700]="channel === 'email'"
                [class.bg-purple-100]="channel === 'sms'" [class.text-purple-700]="channel === 'sms'"
                [class.bg-green-100]="channel === 'whatsapp'" [class.text-green-700]="channel === 'whatsapp'">
                {{ formatChannel(channel) }}
              </span>
              }
            </div>
            @if (rule.smsProvider && rule.channels.includes('sms')) {
            <span class="text-xs text-gray-500 dark:text-gray-400">SMS via <span class="font-medium text-gray-700 dark:text-gray-300">{{ rule.smsProvider }}</span></span>
            }
          </div>
          } @empty {
          <p class="text-sm text-gray-400">No dispatch steps defined.</p>
          }
        </div>
      </div>

      <!-- Rule overrides -->
      <div class="pt-6 first:pt-0">
        <h5 class="mb-1 flex items-center gap-1.5 text-base font-semibold text-black dark:text-white">
          Rule Overrides
          <app-info-tooltip>
            Exceptions to the schedule above for specific segments — e.g. forcing WhatsApp instead of the default
            channel, or using a different SMS provider, only for the listed segments.
          </app-info-tooltip>
        </h5>

        <div class="mt-3 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table class="w-full text-left text-sm text-gray-700 dark:text-gray-300">
            <thead class="bg-gray-50 text-xs uppercase text-gray-500 dark:bg-gray-800 dark:text-gray-400">
              <tr>
                <th class="px-4 py-2.5">Overrides</th>
                <th class="px-4 py-2.5">New Value</th>
                <th class="px-4 py-2.5">Applies To</th>
              </tr>
            </thead>
            <tbody>
              @for (override of ruleOverrides; track $index) {
              <tr class="border-t border-gray-100 dark:border-gray-800">
                <td class="px-4 py-2.5 font-medium text-gray-800 dark:text-gray-200">{{ formatWords(override.element) }}</td>
                <td class="px-4 py-2.5">{{ override.value }}</td>
                <td class="px-4 py-2.5">
                  <div class="flex flex-wrap gap-1.5">
                    @for (segment of override.segments; track segment) {
                    <span class="rounded-full border border-gray-300 bg-gray-50 px-2.5 py-0.5 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">{{ segment }}</span>
                    }
                  </div>
                </td>
              </tr>
              } @empty {
              <tr><td colspan="3" class="px-4 py-6 text-center text-gray-400">No rule overrides defined.</td></tr>
              }
            </tbody>
          </table>
        </div>
      </div>

    </div>
  </div>
  }
</div>
}
